/**
 * Error Message Utilities
 * Provides context-aware, user-friendly error messages for form validation
 */

export type ErrorType = 
  | 'required' 
  | 'invalid' 
  | 'min' 
  | 'max' 
  | 'minLength' 
  | 'maxLength'
  | 'future'
  | 'tooOld'
  | 'pattern'
  | 'custom';

export interface ErrorContext {
  max?: number;
  min?: number;
  maxLength?: number;
  minLength?: number;
  typeName?: string;
  categoryName?: string;
  costCenterName?: string;
  availableUnits?: string[];
  maxDaysAgo?: number;
  average?: number;
  current?: number;
}

type ErrorMessage = string | ((ctx: ErrorContext) => string);

const errorMessages: Record<string, Record<ErrorType | string, ErrorMessage>> = {
  type: {
    required: 'Please select a business type to continue',
    invalid: 'This business type is not available or you do not have access to it'
  },
  
  unit: {
    required: 'Please select your business unit',
    invalid: (ctx) => 
      ctx.availableUnits && ctx.availableUnits.length > 0
        ? `You don't have access to this unit. Available: ${ctx.availableUnits.join(', ')}`
        : 'You do not have access to this business unit'
  },
  
  costCenter: {
    required: 'Please select a cost center for budget allocation',
    invalid: (ctx) => 
      ctx.typeName
        ? `This cost center is not available for ${ctx.typeName}`
        : 'This cost center is not available for the selected type'
  },
  
  category: {
    required: 'Please select an expense category',
    invalid: (ctx) =>
      ctx.costCenterName
        ? `This category is not available for ${ctx.costCenterName}`
        : 'This category is not available for the selected cost center'
  },
  
  date: {
    required: 'Please select the expense date',
    invalid: 'Please enter a valid date (DD/MM/YYYY or use the date picker)',
    future: 'Date cannot be in the future. Please select today or an earlier date.',
    tooOld: (ctx) => 
      `This date is too old. Non-admin users can only enter expenses from the last ${ctx.maxDaysAgo || 5} days.`,
    pattern: 'Please use the date picker or enter date in DD/MM/YYYY format'
  },
  
  amount: {
    required: 'Please enter the expense amount',
    invalid: 'Amount must be a positive number (e.g., 1250.50)',
    min: 'Amount must be greater than ₹0',
    max: (ctx) => 
      `Amount exceeds maximum limit of ₹${ctx.max?.toLocaleString('en-IN') || '1,00,000'}`,
    custom: (ctx) => {
      if (ctx.average && ctx.current && ctx.current > ctx.average * 2) {
        const percentage = Math.round((ctx.current / ctx.average) * 100);
        return `⚠️ This is ${percentage}% of the average (₹${ctx.average.toLocaleString('en-IN')}) for this category. Please verify.`;
      }
      return 'Please verify the amount';
    }
  },
  
  shop: {
    required: 'Please enter the vendor or shop name',
    invalid: 'Vendor name must be between 2-100 characters',
    minLength: 'Vendor name is too short (minimum 2 characters)',
    maxLength: 'Vendor name is too long (maximum 100 characters)'
  },
  
  party: {
    required: 'Please enter the party name',
    invalid: 'Party name must be between 2-100 characters',
    minLength: 'Party name is too short (minimum 2 characters)',
    maxLength: 'Party name is too long (maximum 100 characters)'
  },
  
  description: {
    required: 'Please describe the purpose of this expense',
    invalid: 'Description must be between 10-500 characters',
    minLength: 'Please provide more details about this expense (minimum 10 characters)',
    maxLength: 'Description is too long (maximum 500 characters)'
  },
  
  quantity: {
    required: 'Please enter the quantity',
    invalid: 'Quantity must be a positive number',
    min: 'Quantity must be greater than 0',
    pattern: 'Please enter a valid number (e.g., 5 or 5.5)'
  },
  
  rate: {
    required: 'Please enter the rate per unit',
    invalid: 'Rate must be a positive number',
    min: 'Rate must be greater than ₹0',
    pattern: 'Please enter a valid amount (e.g., 150.50)'
  },
  
  grossAmount: {
    required: 'Please enter the gross amount',
    invalid: 'Gross amount must be a positive number',
    min: 'Gross amount must be greater than ₹0'
  },
  
  gstAmount: {
    invalid: 'GST amount must be a positive number or zero',
    min: 'GST amount cannot be negative'
  },
  
  assetId: {
    minLength: 'Asset ID is too short (minimum 2 characters)',
    maxLength: 'Asset ID is too long (maximum 50 characters)'
  },
  
  vehicleNumber: {
    required: 'Please enter the vehicle registration number',
    invalid: 'Please enter a valid vehicle registration number',
    pattern: 'Please enter a valid vehicle number (e.g., TN01AB1234)'
  }
};

/**
 * Get a user-friendly error message for a field
 */
export const getFieldErrorMessage = (
  fieldName: string,
  error: any,
  context?: ErrorContext
): string => {
  // If no error, return empty string
  if (!error) return '';

  // Get error type
  const errorType: ErrorType | string = error.type || 'invalid';

  // Get field-specific error messages
  const fieldErrors = errorMessages[fieldName];
  if (!fieldErrors) {
    // Fallback to error message if field not found
    return error.message || 'Invalid input';
  }

  // Get error message for this type
  const message = fieldErrors[errorType];
  if (!message) {
    // Fallback to generic invalid message
    return fieldErrors.invalid 
      ? (typeof fieldErrors.invalid === 'function' 
          ? fieldErrors.invalid(context || {})
          : fieldErrors.invalid)
      : error.message || 'Invalid input';
  }

  // If message is a function, call it with context
  if (typeof message === 'function') {
    return message(context || {});
  }

  // Return static message
  return message;
};

/**
 * Get helper text for a field (non-error guidance)
 */
export const getFieldHelperText = (fieldName: string): string => {
  const helperTexts: Record<string, string> = {
    type: 'Select the primary business classification for this expense',
    unit: 'Choose the business unit that should be charged for this expense',
    costCenter: 'Select the department or cost center responsible for this expense',
    category: 'Choose the category that best describes this expense',
    date: 'Select the date when this expense occurred',
    shop: 'Enter the name of the vendor, shop, or supplier',
    party: 'Enter the name of the party involved in this transaction',
    description: 'Describe the purpose and details of this expense (10-500 characters)',
    amount: 'Enter the total amount of this expense',
    quantity: 'Enter the number of units purchased',
    rate: 'Enter the price per unit',
    grossAmount: 'Enter the total amount before GST',
    gstAmount: 'Enter the GST amount (typically 18% of gross amount)',
    assetId: 'Enter vehicle registration, equipment ID, or asset code (optional)',
    vehicleNumber: 'Enter the vehicle registration number (e.g., TN01AB1234)'
  };

  return helperTexts[fieldName] || '';
};

/**
 * Get example text for a field
 */
export const getFieldExample = (fieldName: string): string => {
  const examples: Record<string, string> = {
    amount: 'e.g., 1250.50',
    quantity: 'e.g., 5 or 5.5',
    rate: 'e.g., 150.50',
    shop: 'e.g., ABC Hardware Store',
    party: 'e.g., John Doe',
    description: 'e.g., Purchased 5 bags of cement for construction project',
    assetId: 'e.g., TN01AB1234 or EQ-001',
    vehicleNumber: 'e.g., TN01AB1234'
  };

  return examples[fieldName] || '';
};
